/**
 * Now as a first-class cross-project surface (docs/ux/project-tab-spec.md §1.9): what every
 * agent is doing. One row per run with its project tab, lane, agent/machine/account, live line,
 * unified run state, last activity and landing chip; grouped by attention or by project;
 * filters; a capacity header; bulk Stop / Pause lane / Hand off; keyboard triage; zero state.
 */
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Button, StatusIcon, type StatusKind } from '@viko/transcript-ui'
import {
  CircleCheck,
  Diamond,
  Hand,
  Keyboard,
  MessageSquare,
  Pause,
  Play,
  SlidersHorizontal,
  Square,
} from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { useBackend } from '../../lib/backend.js'
import { landingChips } from '../../lib/landing-status.js'
import { isLiveRun } from '../../lib/lanes-model.js'
import { loadLine, lockedView } from '../../lib/locked-view.js'
import {
  buildNowRows,
  FAMILY_TITLE,
  filterNowRows,
  groupNowRows,
  type NowFamily,
  type NowFilter,
  type NowGroupView,
  type NowRow,
  type TriageAction,
} from '../../lib/now-inbox.js'
import { qk, useLandings, useMachines, useProjects, useRuns } from '../../lib/queries.js'
import { nowRowEntity } from '../../lib/row-actions.js'
import { isTyping } from '../../lib/shortcut.js'
import { type StableShape, useInteracting, useStableGroups } from '../../lib/stable-order.js'
import { withoutTriageRuns } from '../../lib/triage-runs.js'
import { selectedAccount } from '../../lib/usage-accounting.js'
import { useAppUi } from '../app-ui.js'
import { useDecisions } from '../decision/useDecisions.js'
import { RowAge } from '../inbox/InboxTriage.js'
import { useNeedsYou } from '../inbox/useNeedsYou.js'
import { useTabOrder } from '../inbox/useTabOrder.js'
import { useTriageKeys } from '../inbox/useTriageKeys.js'
import { useLivePhases } from '../lanes/LiveRuns.js'
import { useLocked } from '../lock/ContentLock.js'
import { RowActionBar, RowActionsProvider, TRIAGE_KEYS, useRowActions } from '../RowActions.js'
import { errorText, SkeletonRows, useToast } from '../ui.js'
import { SteerBar, type SteerMode } from './SteerBar.js'
import '../inbox/triage.css'

const NOW_SHAPE: StableShape<NowGroupView, NowRow> = {
  groupKey: (g) => g.key,
  entries: (g) => g.rows,
  entryKey: (r) => r.key,
  withEntries: (g, rows) => ({ ...g, rows }),
}

const STATUS_OF: Record<string, StatusKind> = {
  'is-wait': 'wait',
  'is-fail': 'fail',
  'is-run': 'run',
  'is-ok': 'ok',
}

const PHASE_TONE: Partial<Record<string, string>> = {
  waiting: 'is-wait',
  stalled: 'is-fail',
  looping: 'is-fail',
  'blocked-environment': 'is-fail',
  failed: 'is-fail',
  offline: 'is-wait',
  working: 'is-run',
  'making-progress': 'is-run',
  finished: 'is-ok',
  review: 'is-ok',
  ready: 'is-ok',
}

export function NowBoard({
  current,
  currentDecision,
  onOpen,
  onClose,
  onOpenDecision,
  onOpenInbox: openInbox,
  embedded,
  exclusiveKeys,
  projectId,
  keysEnabled = true,
}: {
  current?: string
  /** The decision open in the inspector (`decision:<id>`): its row shows as current. */
  currentDecision?: string
  onOpen: (runId: string) => void
  onClose: () => void
  onOpenDecision: (key: string, intent?: 'reply' | 'delegate') => void
  /** The needs-you line: where the full list lives (defaults to /inbox). */
  onOpenInbox?: () => void
  embedded?: boolean
  exclusiveKeys?: boolean
  /** ia-shell: pre-filter to one project (Home `?project=`, a tab's ◑ badge). */
  projectId?: string
  /** ia-shell: j/k triage keys (off when another list on the page owns them). */
  keysEnabled?: boolean
}) {
  const b = useBackend()
  const qc = useQueryClient()
  const nav = useNavigate()
  const toast = useToast()
  const ui = useAppUi()
  const runs = useRuns()
  const machines = useMachines()
  const projects = useProjects()
  const landings = useLandings()
  const decisions = useDecisions()
  const tabs = useTabOrder()
  const needs = useNeedsYou()
  const [mode, setMode] = useState<'attention' | 'project'>('attention')
  const [filter, setFilter] = useState<NowFilter>(projectId ? { projectId } : {})
  const [filtersOpen, setFiltersOpen] = useState(false)
  // A project tab's own list is pre-filtered by the tab, not by you.
  const filtered = !!(
    (filter.projectId && filter.projectId !== projectId) ||
    filter.agent ||
    filter.family ||
    filter.machineId
  )
  // core-loop: the row whose steer bar is open (`r`, or Steer on phones), and runs stopping
  // optimistically (Undo `u` until the stop is sent).
  const [steering, setSteering] = useState<string>()
  const [stopping, setStopping] = useState<ReadonlySet<string>>(new Set())
  const root = useRef<HTMLDivElement>(null)

  const all = useMemo(() => withoutTriageRuns(runs.data ?? []), [runs.data]) // triage sessions live under their bark
  const live = useMemo(() => all.filter(isLiveRun), [all])
  const { phases, probes } = useLivePhases(live)
  const chips = useMemo(
    () => landingChips(all, landings.data, projects.data, Date.now()),
    [all, landings.data, projects.data],
  )
  const rows = useMemo(
    () =>
      buildNowRows({
        runs: all,
        machines: machines.data ?? [],
        projects: projects.data ?? [],
        decisions: decisions.data ?? [],
        live: phases,
        chips,
        accountOf: (r) => {
          const m = machines.data?.find((x) => x.id === r.machineId)
          const n = (m?.providerAccounts ?? []).filter(
            (a) => a.enabled && a.provider === (r.agent ?? 'claude'),
          ).length
          return n > 1 ? selectedAccount(r, machines.data)?.label : undefined
        },
        now: Date.now(),
      }),
    [all, machines.data, projects.data, decisions.data, phases, chips],
  )
  const shown = useMemo(() => filterNowRows(rows, filter), [rows, filter])
  const liveGroups = useMemo(() => groupNowRows(shown, mode, tabs.order), [shown, mode, tabs.order])
  // Rows keep their place while the pointer or focus is in the list; the new order lands on idle.
  const groups = useStableGroups(liveGroups, useInteracting(root), NOW_SHAPE)
  const keys = useMemo(() => groups.flatMap((g) => g.rows.map((r) => r.key)), [groups])
  const byKey = useMemo(() => new Map(rows.map((r) => [r.key, r])), [rows])

  const onAction = (a: TriageAction) => {
    const row = 'key' in a ? byKey.get(a.key) : undefined
    switch (a.type) {
      case 'open':
        return row ? onOpen(row.runId) : undefined
      case 'close':
        return onClose()
      case 'suggested':
      case 'pick':
        return row?.decisionKey ? onOpenDecision(row.decisionKey) : undefined
      case 'reply':
        // A waiting run answers its ask in words; any other run is steered in place.
        if (row?.decisionKey) return onOpenDecision(row.decisionKey, 'reply')
        return row ? setSteering(row.key) : undefined
      case 'delegate':
        return row?.decisionKey ? onOpenDecision(row.decisionKey, a.type) : undefined
      case 'project':
        if (row?.projectId && projects.data?.some((p) => p.id === row.projectId))
          void nav({ to: '/p/$projectId', params: { projectId: row.projectId } })
        return
      case 'help':
        return ui.openShortcuts?.() // keyboard-shortcuts: ? opens the global cheat sheet
    }
  }
  const triage = useTriageKeys({
    keys,
    onAction,
    root,
    exclusive: !!exclusiveKeys,
    enabled: keysEnabled,
  })
  const onOpenInbox = openInbox ?? (() => void nav({ href: '/home?v=needs' }))
  const selected = [...triage.selected].map((k) => byKey.get(k)).filter((r): r is NowRow => !!r)

  const bulk = async (label: string, fn: (r: NowRow) => Promise<unknown>, list: NowRow[]) => {
    let n = 0
    for (const r of list)
      try {
        await fn(r)
        n++
      } catch (e) {
        toast(errorText(e), 'error')
      }
    if (n) toast(`${label}: ${n} ${n === 1 ? 'run' : 'runs'}`)
    triage.setSelected([])
    void qc.invalidateQueries({ queryKey: qk.runs })
  }
  /**
   * Optimistic stop with Undo instead of a confirm: the rows show "Stopping", and the stop is
   * sent when the Undo window closes. Undo (`u`, or the toast's button) keeps them running.
   */
  const UNDO_MS = 4000
  const stopLater = (list: NowRow[]) => {
    if (!list.length) return
    const ids = list.map((r) => r.runId)
    setStopping((cur) => new Set([...cur, ...ids]))
    triage.setSelected([])
    const release = () => setStopping((cur) => new Set([...cur].filter((id) => !ids.includes(id))))
    const timer = setTimeout(() => {
      release()
      void bulk(
        'Stopped',
        async (r) => {
          await b.dispatch('run.interrupt', r.machineId, { runId: r.runId, cancelQueued: true })
        },
        list,
      )
    }, UNDO_MS)
    toast(
      list.length === 1
        ? `Stopping ${list[0]?.title ?? 'the run'}`
        : `Stopping ${list.length} runs`,
      'ok',
      {
        ms: UNDO_MS,
        action: {
          label: 'Undo',
          key: 'u',
          run: () => {
            clearTimeout(timer)
            release()
            toast(list.length === 1 ? 'Kept running' : `Kept ${list.length} runs running`)
          },
        },
      },
    )
  }
  const stopRef = useRef(stopLater)
  stopRef.current = stopLater
  const steer = async (row: NowRow, text: string, mode: SteerMode): Promise<boolean> => {
    try {
      await b.dispatch('run.message', row.machineId, {
        runId: row.runId,
        text,
        // ⏎ steers: delivered at the next safe point. ⌘⏎ waits until this step ends.
        queue: mode === 'later' && isBusy(row),
        clientMessageId: crypto.randomUUID(),
      })
      const who = row.agentLabel.split(' ')[0] ?? 'the agent'
      toast(mode === 'later' ? `${who} gets it after this step` : `Sent to ${who}`)
      void qc.invalidateQueries({ queryKey: qk.runs })
      return true
    } catch (e) {
      toast(errorText(e), 'error')
      return false
    }
  }
  // Esc returns focus to the row the steer bar opened from.
  const closeSteer = (key: string) => {
    setSteering(undefined)
    requestAnimationFrame(() =>
      root.current
        ?.querySelector<HTMLElement>(`[data-row-key="${CSS.escape(key)}"] [data-row-main]`)
        ?.focus(),
    )
  }
  // A run row's registry verbs on this list: Steer opens the bar, Stop is optimistic with Undo.
  const handlers = useMemo(
    () => ({
      reply: (e: { id: string }) => {
        const row = rows.find((r) => r.runId === e.id)
        if (row) setSteering(row.key)
      },
      stop: (e: { id: string }) => {
        const row = rows.find((r) => r.runId === e.id)
        if (row) stopRef.current([row])
      },
    }),
    [rows],
  )
  const handOff = (list: NowRow[]) =>
    bulk(
      'Asked to hand off',
      (r) =>
        b.dispatch('run.message', r.machineId, {
          runId: r.runId,
          text: 'Please wrap up and hand off now with viko_handoff (summary and next steps), then stop.',
          queue: true,
        }),
      list,
    )
  const pauseLanes = async (list: NowRow[]) => {
    const lanes = [
      ...new Set(
        list.map((r) => all.find((x) => x.runId === r.runId)?.workstreamId).filter(Boolean),
      ),
    ] as string[]
    let n = 0
    for (const id of lanes)
      try {
        await b.api.updateWorkstream(id, { paused: true })
        n++
      } catch (e) {
        toast(errorText(e), 'error')
      }
    if (n) toast(`Paused ${n} ${n === 1 ? 'lane' : 'lanes'}: no new tasks start there`)
    triage.setSelected([])
  }

  const machineList = machines.data ?? []
  const idle = machineList.filter((m) => m.online && m.caps && m.caps.running < m.caps.maxRuns)
  const unseen = lockedView([], machineList).machines
  const locked = useLocked()
  return (
    <RowActionsProvider {...(projectId ? { projectId } : {})} handlers={handlers}>
      <div className={`tr${triage.selected.size ? ' is-selecting' : ''}`} ref={root}>
        <div className="tr-tools">
          {needs.total ? (
            // The app-wide needs-you count (lib/needs-you.ts): the same number as the Inbox badge.
            <p className="tr-needs" data-testid="now-needs-you">
              <Diamond size={13} aria-hidden="true" className="text-status-wait" />
              <button type="button" className="tr-needs__link" onClick={() => onOpenInbox()}>
                {needs.total} {needs.total === 1 ? 'needs' : 'need'} you
              </button>
            </p>
          ) : null}
          <button
            type="button"
            className="tr-filter-btn"
            aria-expanded={filtersOpen || filtered}
            onClick={() => setFiltersOpen((x) => !x)}
          >
            <SlidersHorizontal size={14} aria-hidden="true" />
            Filter
          </button>
        </div>
        {filtersOpen || filtered ? (
          <div className="tr-controls" role="toolbar" aria-label="Group and filter">
            <div className="tr-seg" role="group" aria-label="Group by">
              {(['attention', 'project'] as const).map((m) => (
                <button key={m} type="button" aria-pressed={mode === m} onClick={() => setMode(m)}>
                  {m === 'attention' ? 'By attention' : 'By project'}
                </button>
              ))}
            </div>
            <label className="sr-only" htmlFor="now-f-project">
              Project
            </label>
            <select
              id="now-f-project"
              className="tr-select"
              value={filter.projectId ?? ''}
              onChange={(e) => setFilter((f) => ({ ...f, projectId: e.target.value || undefined }))}
            >
              <option value="">All projects</option>
              {(projects.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <label className="sr-only" htmlFor="now-f-agent">
              Agent
            </label>
            <select
              id="now-f-agent"
              className="tr-select"
              value={filter.agent ?? ''}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  agent: (e.target.value || undefined) as NowFilter['agent'],
                }))
              }
            >
              <option value="">All agents</option>
              <option value="claude">Claude</option>
              <option value="codex">Codex</option>
            </select>
            <label className="sr-only" htmlFor="now-f-state">
              State
            </label>
            <select
              id="now-f-state"
              className="tr-select"
              value={filter.family ?? ''}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  family: (e.target.value || undefined) as NowFamily | undefined,
                }))
              }
            >
              <option value="">Every state</option>
              {(Object.keys(FAMILY_TITLE) as NowFamily[]).map((f) => (
                <option key={f} value={f}>
                  {FAMILY_TITLE[f]}
                </option>
              ))}
            </select>
            <span className="tr-grow" />
            {embedded ? null : (
              <button
                type="button"
                className="vk-btn vk-btn--ghost vk-btn--sm"
                aria-keyshortcuts="?"
                onClick={() => ui.openShortcuts?.()}
              >
                <Keyboard size={14} aria-hidden="true" />
                <span>Shortcuts</span>
              </button>
            )}
          </div>
        ) : null}

        {runs.isPending ? <SkeletonRows n={5} /> : null}
        {runs.isSuccess && !rows.length && unseen.length ? (
          // One source: the machines' own reports say runs are in use that this browser has no
          // record of (started elsewhere, or not readable here). Never "Nothing running" then.
          <section className="tr-zero" aria-labelledby="now-zero-h" data-testid="now-unseen">
            <p className="tr-zero__h" id="now-zero-h">
              {unseen.map(loadLine).join(' · ')}
            </p>
            <p>
              {locked
                ? 'Unlock this browser to see which runs they are.'
                : 'This browser has no record of them yet: they were started from another device or before it was set up.'}
            </p>
          </section>
        ) : null}
        {runs.isSuccess && !rows.length && !unseen.length ? (
          <section className="tr-zero" aria-labelledby="now-zero-h">
            <p className="tr-zero__h" id="now-zero-h">
              <CircleCheck size={20} aria-hidden="true" /> Nothing running
            </p>
            <p>
              {idle.length
                ? `${idle.map((m) => m.name).join(' and ')} ${idle.length === 1 ? 'has' : 'have'} free slots.`
                : 'All caught up.'}
            </p>
            <Button
              variant="primary"
              size="sm"
              icon={<Play size={14} />}
              onClick={() => ui.openStart()}
            >
              Start a session
            </Button>
          </section>
        ) : null}
        {rows.length && !shown.length ? (
          <div className="tr-zero">
            <p className="tr-zero__h">No runs match these filters</p>
            <button
              type="button"
              className="vk-btn vk-btn--secondary vk-btn--sm"
              onClick={() => setFilter({})}
            >
              Show everything
            </button>
          </div>
        ) : null}

        {groups.map((g) => (
          <section key={g.key} className="tr-group" aria-labelledby={`now-g-${g.key}`}>
            <h2 id={`now-g-${g.key}`} className={`tr-group__h${g.hot ? ' is-hot' : ''}`}>
              <span className="tr-group__t">{g.title}</span>
              {/* Runs, not needs-you items: the unit is spelled out so it never reads as a badge. */}
              <span className="tr-group__n">
                {g.rows.length} {g.rows.length === 1 ? 'run' : 'runs'}
              </span>
            </h2>
            <ul className="tr-list">
              {g.rows.map((r) => (
                <NowRowView
                  key={r.key}
                  row={r}
                  focused={keys[triage.focus] === r.key}
                  current={
                    current === r.runId || (!!r.decisionKey && r.decisionKey === currentDecision)
                  }
                  selected={triage.selected.has(r.key)}
                  onOpen={() => {
                    triage.setFocus(keys.indexOf(r.key))
                    onOpen(r.runId)
                  }}
                  onToggle={() => triage.toggle(r.key)}
                  onDecision={() => r.decisionKey && onOpenDecision(r.decisionKey)}
                  stopping={stopping.has(r.runId)}
                  steering={steering === r.key}
                  onSteer={() => setSteering(r.key)}
                  steerBar={
                    steering === r.key ? (
                      <SteerBar
                        title={r.title}
                        agent={r.agent === 'codex' ? 'Codex' : 'Claude'}
                        busy={isBusy(r)}
                        onSend={(text, mode) => steer(r, text, mode)}
                        onStop={() => {
                          closeSteer(r.key)
                          stopLater([r])
                        }}
                        onClose={() => closeSteer(r.key)}
                      />
                    ) : null
                  }
                />
              ))}
            </ul>
          </section>
        ))}

        {selected.length ? (
          <div className="tr-bulk" role="region" aria-label="Selected runs">
            <span className="tr-bulk__n">{selected.length} selected</span>
            <button
              type="button"
              className="vk-btn vk-btn--secondary vk-btn--sm"
              onClick={() => stopLater(selected)}
            >
              <Square size={13} aria-hidden="true" />
              <span>Stop</span>
            </button>
            <button
              type="button"
              className="vk-btn vk-btn--secondary vk-btn--sm"
              onClick={() => void pauseLanes(selected)}
            >
              <Pause size={13} aria-hidden="true" />
              <span>Pause lane</span>
            </button>
            <button
              type="button"
              className="vk-btn vk-btn--secondary vk-btn--sm"
              onClick={() => void handOff(selected)}
            >
              <Hand size={13} aria-hidden="true" />
              <span>Hand off</span>
            </button>
            <button
              type="button"
              className="vk-btn vk-btn--ghost vk-btn--sm"
              onClick={() => triage.setSelected([])}
            >
              Clear
            </button>
          </div>
        ) : null}
        {machineList.length ? (
          <section className="tr-capacity" aria-label="Capacity">
            {machineList.map((m) => (
              <button
                key={m.id}
                type="button"
                className="tr-machine"
                aria-pressed={filter.machineId === m.id}
                onClick={() =>
                  setFilter((f) => ({ ...f, machineId: f.machineId === m.id ? undefined : m.id }))
                }
              >
                <strong>{m.name}</strong>
                <span>
                  {!m.online
                    ? 'Offline'
                    : m.caps
                      ? `${m.caps.running} of ${m.caps.maxRuns} runs in use`
                      : 'Online'}
                </span>
              </button>
            ))}
          </section>
        ) : null}
        {probes}
      </div>
    </RowActionsProvider>
  )
}

/** The phase in words when the glyph alone is ambiguous ("In a terminal", "Stalled", "Ready to
 * land"); plain running and waiting rows say nothing more (the glyph and the live line do). */
function phaseWord(r: NowRow): string | undefined {
  if (r.phase === 'waiting' || r.phase === 'working' || r.phase === 'making-progress')
    return r.phaseLabel === 'In a terminal' ? r.phaseLabel : undefined
  return r.phaseLabel
}

/** Mid-turn: a message waits behind the current step. */
const isBusy = (r: NowRow) => r.live && (r.family === 'working' || r.family === 'progress')

function NowRowView({
  row,
  focused,
  current,
  selected,
  onOpen,
  onToggle,
  onDecision,
  stopping,
  steering,
  onSteer,
  steerBar,
}: {
  row: NowRow
  focused: boolean
  current: boolean
  selected: boolean
  onOpen: () => void
  onToggle: () => void
  onDecision: () => void
  stopping: boolean
  steering: boolean
  onSteer: () => void
  steerBar: React.ReactNode
}) {
  const press = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const acts = useRowActions(nowRowEntity(row), { skipKeys: TRIAGE_KEYS })
  const tone = PHASE_TONE[row.phase] ?? ''
  const streaming = row.family === 'progress' || row.family === 'working'
  return (
    <li
      className={`tr-row${focused ? ' is-focused' : ''}${current ? ' is-current' : ''}${selected ? ' is-selected' : ''}`}
      data-row-key={row.key}
      data-run={row.runId}
      {...acts.rowProps}
      onKeyDown={(e) => {
        // `r` steers from the focused row even where the list's triage keys are off (Home).
        if (
          e.key === 'r' &&
          !e.defaultPrevented &&
          !e.metaKey &&
          !e.ctrlKey &&
          !e.altKey &&
          !isTyping(e.nativeEvent)
        ) {
          // A run waiting on an ask answers it; any other run is steered in place.
          e.preventDefault()
          if (row.decisionKey) onDecision()
          else onSteer()
          return
        }
        acts.rowProps.onKeyDown(e)
      }}
      {...(stopping ? { 'data-stopping': '' } : {})}
      data-family={row.family}
      {...(row.decisionKey ? { 'data-decision-key': row.decisionKey } : {})}
      onPointerDown={(e) => {
        if (e.pointerType !== 'touch') return
        press.current = setTimeout(() => {
          press.current = undefined
          onToggle()
        }, 550)
      }}
      onPointerUp={() => clearTimeout(press.current)}
      onPointerCancel={() => clearTimeout(press.current)}
      onPointerMove={() => clearTimeout(press.current)}
    >
      <span className="tr-row__lead">
        <span className={`tr-glyph vk-icol ${tone}`}>
          <StatusIcon
            kind={STATUS_OF[tone] ?? (streaming ? 'run' : 'idle')}
            label={row.phaseLabel}
          />
        </span>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Select ${row.title}`}
          tabIndex={-1}
        />
      </span>
      <button
        type="button"
        className="tr-row__main"
        data-row-main
        aria-label={`${row.title}. ${row.phaseLabel}${row.projectName ? `, ${row.projectName}` : ''}`}
        aria-current={current ? 'true' : undefined}
        tabIndex={focused || current ? 0 : -1}
        onClick={onOpen}
      >
        <span className="tr-row__title">{row.title}</span>
        {/* app-clarity: one meta sentence — project · what it is doing now · agent. The phase
            is the glyph (and its label); only Stopping is a word, in its status colour. */}
        <span className="tr-row__meta">
          {stopping ? <b className="tr-stopping">Stopping (u to undo) · </b> : null}
          {!stopping && phaseWord(row) ? (
            <b className={`tr-phase ${tone}`}>{phaseWord(row)} · </b>
          ) : null}
          {[
            row.projectName,
            row.phase === 'waiting'
              ? row.liveLine.replace(/^Waiting for you:\s*/, '')
              : row.liveLine,
            row.agentLabel,
            row.chip?.label,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </button>
      <span className="tr-row__side">
        <RowAge ts={row.lastActivity} />
      </span>
      {!row.decisionKey && !steering ? (
        // core-loop: Steer is one tap on phones and `r` on the focused row; it opens in place.
        <span className="tr-row__acts">
          <button
            type="button"
            className="vk-btn vk-btn--secondary vk-btn--sm"
            aria-label={`Steer: ${row.title}`}
            aria-keyshortcuts="r"
            onClick={onSteer}
          >
            <MessageSquare size={12} aria-hidden="true" />
            <span>Steer</span>
            <kbd>r</kbd>
          </button>
        </span>
      ) : null}
      {steerBar}
      {row.decisionKey ? (
        // Inside the row gutter at every width (under the meta line), reachable with Tab and
        // with the row's `s` / `1–9` keys; opens the decision in place.
        <span className="tr-row__acts">
          <button
            type="button"
            className="vk-btn vk-btn--secondary vk-btn--sm"
            aria-label={`Decide: ${row.title}`}
            aria-keyshortcuts="s"
            onClick={onDecision}
          >
            <Diamond size={12} aria-hidden="true" />
            <span>Decide</span>
          </button>
        </span>
      ) : null}
      <RowActionBar api={acts} label={row.title} />
    </li>
  )
}
